import { useState, type FormEvent } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Droplets } from "lucide-react";
import { Spinner } from "../board/Spinner";
import { useAuth } from "../auth/AuthProvider";
import { friendlyAuthError } from "../auth/friendlyAuthError";

export const Route = createFileRoute("/signup")({ component: SignUpRoute });

// Public self-serve sign-up: creates a Firebase account with no memberships. A deployed
// fail-closed gate stops a new business from sending billable SMS until the maintainer
// activates it, so opening this door is safe. What happens next is not this screen's
// concern — it navigates to "/" and ScopeGate decides what a no-membership user sees
// (today: nothing useful; a later task routes that state to an onboarding wizard).
function SignUpRoute() {
  const auth = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setPending(true);
    try {
      await auth.signUp(email.trim(), password);
      void navigate({ to: "/" });
    } catch (err) {
      setError(friendlyAuthError(err));
      setPending(false); // stays mounted on failure, so the button must be usable again
    }
  };

  return (
    <div className="min-h-screen grid place-items-center p-6" style={{ background: "#eef2f4" }}>
      <div className="w-full max-w-sm space-y-4 rounded-2xl bg-white p-6 shadow-lg">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white" style={{ background: "#0fa3b1" }}>
            <Droplets size={20} />
          </div>
          <h1 className="text-lg font-bold text-slate-800">Create your account</h1>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <label className="block text-sm font-medium text-slate-700">
            Email
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              autoComplete="username" required
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-slate-900"
            />
          </label>

          <label className="block text-sm font-medium text-slate-700">
            Password
            <input
              type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password" required
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2.5 text-slate-900"
            />
          </label>

          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}

          {/* src/apps/CLAUDE.md async-feedback rule: disabled + inline spinner while in flight,
              with the label swapped for the spinner so the button does not resize. */}
          <button
            type="submit" disabled={pending}
            className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 font-semibold text-white shadow disabled:opacity-60"
            style={{ background: "#0fa3b1" }}
          >
            {pending ? <Spinner /> : "Create account"}
          </button>
        </form>
      </div>
    </div>
  );
}
